'use client'

import Script from 'next/script'
import { useCallback, useEffect, useRef, useState, type TouchEvent } from 'react'

import { IconChevLeft, IconChevRight } from '@/app/components/atoms/Icons'
import { useYouTubeIframeApi } from '@/app/lib/hooks/useYouTubeIframeApi'

import { MAX_PEEK_LARGE, MAX_PEEK_SMALL, MIN_VIDEOS, PEEK_THRESHOLD, SECTION_LABEL } from './Video.constants'
import type { VideoItem } from './Video.types'
import styles from './VideoClient.module.scss'
import VideoCard from './VideoCard'

interface VideoClientProps {
  /** Ya normalizados y validados (`resolveVideoItems`) — al menos
   * `MIN_VIDEOS`, garantizado por `Video.tsx` antes de montar este
   * componente. */
  items: VideoItem[]
}

/** Distancia mínima de swipe horizontal (mobile/tablet) para cambiar de
 * tarjeta — mismo criterio que el carrusel de referencia
 * (`EventosDestacados`). */
const SWIPE_THRESHOLD_PX = 40

/**
 * Carrusel circular con efecto "coverflow" — fiel al carrusel de referencia
 * (`EventosDestacados`, Figma "Vídeo"): la tarjeta activa reproduce su
 * vídeo real; hasta `MAX_PEEK_LARGE`/`MAX_PEEK_SMALL` tarjetas asoman a
 * cada lado según el total de vídeos válidos (FR-009/010). Navegación por
 * flechas, swipe táctil, clic directo en una tarjeta que asoma, o avance
 * automático al terminar el vídeo activo (mismo desplazamiento circular que
 * la referencia, pero disparado por el final real del vídeo en vez de un
 * temporizador fijo — ese proyecto no reproduce vídeo real). Descarta en
 * caliente cualquier ítem cuyo reproductor falle (FR-017).
 */
export default function VideoClient({ items: initialItems }: VideoClientProps) {
  const [items, setItems] = useState(initialItems)
  const [activeIndex, setActiveIndex] = useState(0)
  // Sonido compartido por todo el carrusel, no por tarjeta: si el usuario
  // activa el sonido en un vídeo, el siguiente que se reproduzca también
  // arranca con sonido (y viceversa) — ver `useActiveVideoPlayer`.
  const [isMuted, setIsMuted] = useState(true)
  const touchStartXRef = useRef<number | null>(null)

  // `isInView`: fuera del viewport el vídeo activo se pausa y se reanuda al
  // volver (ver `useActiveVideoPlayer`). `hasEnteredView`: hasta que el
  // carrusel aparece por primera vez en pantalla no se carga NADA de YouTube
  // (ni el script de la API ni ningún reproductor/iframe) — el módulo suele
  // estar por debajo del pliegue y, mientras, basta con las miniaturas.
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const [isInView, setIsInView] = useState(false)
  const [hasEnteredView, setHasEnteredView] = useState(false)
  useEffect(() => {
    const node = wrapRef.current
    if (!node) return undefined
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1]
      if (!entry) return
      setIsInView(entry.isIntersecting)
      if (entry.isIntersecting) setHasEnteredView(true)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  // Cargado una sola vez para todo el carrusel (no por tarjeta) — mismo
  // patrón que `HeroCarousel`. `canMountPlayer` cubre también el caso de que
  // la API ya esté cargada por otro componente (p. ej. el hero): aun así, no
  // se crea el reproductor hasta que el carrusel entra en la vista.
  const { ready: apiReady, shouldRenderScript } = useYouTubeIframeApi(hasEnteredView)
  const canMountPlayer = apiReady && hasEnteredView

  const total = items.length
  const maxPeek = total >= PEEK_THRESHOLD ? MAX_PEEK_LARGE : MAX_PEEK_SMALL

  const goTo = useCallback((index: number) => {
    setActiveIndex((current) => {
      const len = items.length
      if (len === 0) return current
      return ((index % len) + len) % len
    })
  }, [items.length])

  const handleItemError = (id: string) => {
    setItems((current) => current.filter((item) => item.id !== id))
  }

  const handleTouchStart = (event: TouchEvent) => {
    touchStartXRef.current = event.touches[0]?.clientX ?? null
  }

  const handleTouchEnd = (event: TouchEvent) => {
    const startX = touchStartXRef.current
    touchStartXRef.current = null
    if (startX == null) return
    const endX = event.changedTouches[0]?.clientX ?? startX
    const delta = endX - startX
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return
    goTo(activeIndex + (delta < 0 ? 1 : -1))
  }

  // Si el descarte en caliente de un ítem con error (FR-017) deja el
  // carrusel por debajo del mínimo, se oculta igual que si nunca hubiera
  // llegado a tener suficientes vídeos válidos.
  if (items.length < MIN_VIDEOS) return null

  return (
    <>
      {shouldRenderScript && (
        <Script src="https://www.youtube.com/iframe_api" strategy="afterInteractive" />
      )}

      <div ref={wrapRef} className={styles.wrap} role="region" aria-label={SECTION_LABEL}>
        <div className={styles.stage} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          {/* Flechas solo desde desktop — en mobile/tablet se navega por
              swipe o tocando una tarjeta que asoma, igual que la referencia
              (`EventosDestacados`, clase `.desktopArrow`). */}
          <button
            type="button"
            className={`${styles.arrow} ${styles.arrowLeft}`}
            onClick={() => goTo(activeIndex - 1)}
            aria-label="Vídeo anterior"
          >
            <IconChevLeft size={18} />
          </button>
          <button
            type="button"
            className={`${styles.arrow} ${styles.arrowRight}`}
            onClick={() => goTo(activeIndex + 1)}
            aria-label="Vídeo siguiente"
          >
            <IconChevRight size={18} />
          </button>

          {items.map((item, index) => {
            const len = items.length
            let rel = index - activeIndex
            if (rel > len / 2) rel -= len
            if (rel < -len / 2) rel += len
            // Con más vídeos válidos que huecos visibles
            // (`total > maxPeek * 2 + 1`), las que exceden el peek se
            // mantienen montadas pero colapsadas al centro y por detrás de
            // la activa (ver `VideoCard`: prop `hidden`) — así, al
            // recuperar un hueco visible, la tarjeta crece y se desliza
            // desde detrás de la pila en vez de aparecer de golpe. Nunca
            // ocupan un hueco visible propio ni son clicables mientras
            // están así (a diferencia de la reserva con opacidad que se
            // probó y se descartó por mostrar una tarjeta de fondo visible
            // de más durante la transición).
            const hidden = Math.abs(rel) > maxPeek

            return (
              <VideoCard
                key={item.id}
                item={item}
                index={index}
                rel={rel}
                isActive={rel === 0}
                hidden={hidden}
                apiReady={canMountPlayer}
                isInView={isInView}
                isMuted={isMuted}
                onToggleMute={() => setIsMuted((current) => !current)}
                onSelect={() => goTo(index)}
                onError={handleItemError}
                onEnded={() => goTo(index + 1)}
              />
            )
          })}
        </div>
      </div>
    </>
  )
}
